import Breadcrumbs from 'Components/Breadcrumbs'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import TariffDetails from 'Services/ProductManagement/Features/Tariff/Components/TariffDetails'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import useSingleTariff from 'Services/ProductManagement/Features/Tariff/Hooks/useSingleTariff'
import {
  copySingleTariff,
  fetchAllTariffs,
  type Tariff
} from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { isTariffEditAllowed } from 'Services/ProductManagement/Features/Tariff/utils'
import { useAppDispatch } from 'Store'
import { All_TARIFFS_COUNT_PER_REQUEST, PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import ContentCopy from '@mui/icons-material/ContentCopy'
import { Box, Button, Grid } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function TariffDetailPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const { id } = useParams()
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const dispatch = useAppDispatch()
  const { setSelectedTariffId } = useContext(TariffContext)

  const { singleTariff, singleLoading, submitLoading } = useSingleTariff(id)

  const tariffTitle = singleTariff ? singleTariff.description : ''
  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: tariffTitle
    },
    {
      title: t`Tariff details`
    }
  ]

  const handleCopiedTariffSuccess = (copiedTariff: Tariff) => {
    navigate(`${baseUrl}/${copiedTariff.id}/details`)
    // Fetch all tariffs to update sibebar's tariff list
    dispatch(fetchAllTariffs({ bypass_pagination: true, count: All_TARIFFS_COUNT_PER_REQUEST }))
    setSelectedTariffId(copiedTariff.id)
  }

  const copySelectedTariff = (tariffId: string) => {
    dispatch(copySingleTariff({ id: tariffId }))
      .then(unwrapResult)
      .then((tariffResponse: Tariff) => {
        handleCopiedTariffSuccess(tariffResponse)
        SnackbarUtils.success(t`Tariff was successfully copied.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error copying the tariff.`)
      })
  }

  const isLoading = singleLoading === 'pending'

  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Breadcrumbs items={breadcrumbs} />
          <PageTitle>{t`Tariff details`}</PageTitle>
          <Box display="flex" justifyContent="end" columnGap={2}>
            {singleTariff && isTariffEditAllowed(singleTariff) && (
              <CustomLoadingButton
                size="medium"
                isLoading={isSubmitLoading}
                icon={<ContentCopy />}
                iconPosition="start"
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: t`Are you sure you want to make a copy of ${tariffTitle}?`,
                    handleConfirm: () => {
                      copySelectedTariff(singleTariff.id)
                    }
                  })
                }
              >{t`Copy tariff`}</CustomLoadingButton>
            )}
            {isTariffEditAllowed(singleTariff) && (
              <Button
                variant="outlined"
                size="medium"
                color="inherit"
                onClick={() => navigate(`${baseUrl}/${id}/edit`)}
              >
                {t`Edit tariff`}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <WithLoader loading={isLoading}>
            {singleTariff && <TariffDetails tariff={singleTariff} />}
          </WithLoader>
        </Grid>
      </Grid>
    </Box>
  )
}
